import { useEffect } from "react";

import PropTypes from "prop-types";
import { Controller, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { Paper, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";

import { RoleSelect } from "../../roles";
import { StatusEnum, statusLabels } from "../../shared";
import { CenteredLoading, CustomInputField, CustomSelect } from "../../ui";
import { PlatformEnum, platformLabels } from "../platform";
import { DEFAULT_VALUES, VALIDATION_SCHEMA } from "./user-form-constants";

export const UserForm = ({
  defaultValues,
  onSubmit,
  renderActionButton,
  isLoading = false,
}) => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  useEffect(() => {
    if (!defaultValues) {
      return;
    }

    reset(defaultValues);
  }, [defaultValues, reset]);

  const onFormSubmit = async (formData) => {
    return await onSubmit({
      ...formData,
      roleId: formData.role.id,
    });
  };

  return isLoading ? (
    <CenteredLoading sx={{ height: "100vh" }} />
  ) : (
    <Stack
      component="form"
      noValidate
      onSubmit={handleSubmit(onFormSubmit)}
      spacing={3}
    >
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <CustomInputField
              required
              control={control}
              name="name"
              label="Nama"
              register={register}
              helperText={errors.name?.message}
              error={Boolean(errors.name)}
              InputProps={{
                placeholder: "Cth: John Doe",
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <CustomInputField
              required
              control={control}
              name="username"
              label="Username"
              register={register}
              helperText={errors.username?.message}
              error={Boolean(errors.username)}
              InputProps={{
                placeholder: "Cth: johndoe",
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <CustomInputField
              required
              control={control}
              name="email"
              label="Email"
              register={register}
              helperText={errors.email?.message}
              error={Boolean(errors.email)}
              InputProps={{
                placeholder: "Cth: johndoe@mail.com",
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <CustomInputField
              required
              control={control}
              name="contactNumber"
              label="Nomor Kontak"
              register={register}
              helperText={errors.contactNumber?.message}
              error={Boolean(errors.contactNumber)}
              InputProps={{
                placeholder: "Cth: 62812xxxx",
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <Controller
              control={control}
              name="role"
              render={({ field, fieldState }) => (
                <RoleSelect
                  value={field.value}
                  onChange={field.onChange}
                  TextFieldProps={{
                    required: true,
                    label: "Role",
                    error: fieldState.invalid,
                    helperText: fieldState.error?.message,
                    InputProps: {
                      placeholder: "Pilih",
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <Controller
              control={control}
              name="platform"
              render={({ field, fieldState }) => (
                <CustomSelect
                  required
                  value={field.value}
                  onChange={field.onChange}
                  id="platform"
                  label="Platform"
                  options={Object.values(PlatformEnum).map((value) => ({
                    value: value,
                    label: platformLabels[value],
                  }))}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <Controller
              control={control}
              name="status"
              render={({ field, fieldState }) => (
                <CustomSelect
                  required
                  value={field.value}
                  onChange={field.onChange}
                  id="status"
                  label="Status"
                  options={Object.values(StatusEnum).map((value) => ({
                    value: value,
                    label: statusLabels[value],
                  }))}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>
      {renderActionButton({ isSubmitting })}
    </Stack>
  );
};

UserForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
  renderActionButton: PropTypes.func,
  isLoading: PropTypes.bool,
};
